import { useEffect, useState } from "react";
import { Users, Upload, History, Plus, Trash2, Save, Mail, Wand2, CheckCircle2, AlertTriangle, Loader2, FileUp } from "lucide-react";
import { apiFetch, apiUpload } from "@/lib/api";
import { Eyebrow, PageTitle, PageSubtitle, Loader, EmptyState, Card } from "@/components/common";
import { cn } from "@/lib/utils";

const TABS = [["quote", "Vettori & Quote", Users], ["import", "Importa programma", Upload], ["storico", "Storico", History]];

function StatusBadge({ status }) {
  const map = { registered: ["Registrato", "bg-[hsl(var(--success))] text-white"], invited: ["Invito inviato", "bg-secondary text-secondary-foreground"], pending_invite: ["In attesa", "bg-secondary text-secondary-foreground"] };
  const [label, cls] = map[status] || ["In attesa", "bg-secondary text-secondary-foreground"];
  return <span className={cn("rounded-full px-2.5 py-1 text-[11px] font-extrabold", cls)}>{label}</span>;
}

function QuoteEditor({ carrier, summary, onSaved }) {
  const [rows, setRows] = useState(carrier.quotes?.length ? carrier.quotes : []);
  const [auto, setAuto] = useState(!!carrier.auto_loading);
  const [manual, setManual] = useState(carrier.manual_loading !== false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const addRow = () => setRows((r) => [...r, { route: "", percent: 0, requirement: "", active: true }]);
  const upd = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const del = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await apiFetch(`/dispatch/carriers/${carrier.id}/quotes`, { method: "PUT", body: JSON.stringify({ quotes: rows.map((x) => ({ ...x, percent: Number(x.percent) || 0 })) }) });
      await apiFetch(`/dispatch/carriers/${carrier.id}/tech`, { method: "PATCH", body: JSON.stringify({ auto_loading: auto, manual_loading: manual }) });
      setMsg("Salvato");
      onSaved?.();
    } catch (e) {
      setMsg(e.message || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap gap-3">
        <label className="flex items-center gap-2 text-xs font-bold text-card-foreground">
          <input type="checkbox" data-testid={`tech-auto-${carrier.id}`} checked={auto} onChange={(e) => setAuto(e.target.checked)} /> Semirimorchio/rulliera automatico
        </label>
        <label className="flex items-center gap-2 text-xs font-bold text-card-foreground">
          <input type="checkbox" checked={manual} onChange={(e) => setManual(e.target.checked)} /> Carico manuale
        </label>
      </div>
      <p className="text-[11px] font-extrabold tracking-wide text-primary">QUOTE VIAGGI PER TRATTA</p>
      {rows.map((row, i) => {
        const sum = summary.find((s) => s.route.trim().toLowerCase() === (row.route || "").trim().toLowerCase());
        return (
          <div key={i} className="space-y-1 rounded-xl border border-border bg-secondary/30 p-2.5">
            <div className="flex gap-2">
              <input data-testid={`quote-route-${carrier.id}-${i}`} value={row.route} onChange={(e) => upd(i, "route", e.target.value)} placeholder="Tratta (es. Latina)" className="min-h-[42px] flex-1 rounded-lg border border-border bg-card px-2.5 text-sm outline-none focus:border-primary" />
              <div className="flex items-center rounded-lg border border-border bg-card px-2">
                <input data-testid={`quote-percent-${carrier.id}-${i}`} value={row.percent} onChange={(e) => upd(i, "percent", e.target.value)} inputMode="numeric" className="w-14 bg-transparent text-right text-sm outline-none" />
                <span className="text-sm font-bold text-muted-foreground">%</span>
              </div>
              <button onClick={() => del(i)} className="px-1 text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="flex items-center gap-2">
              <input value={row.requirement || ""} onChange={(e) => upd(i, "requirement", e.target.value)} placeholder="Requisito (es. automatico)" className="min-h-[36px] flex-1 rounded-lg border border-border bg-card px-2.5 text-xs outline-none" />
              <label className="flex items-center gap-1.5 text-[11px] font-bold text-muted-foreground"><input type="checkbox" checked={row.active !== false} onChange={(e) => upd(i, "active", e.target.checked)} /> attiva</label>
            </div>
            {sum && Math.round(sum.total_percent) !== 100 ? (
              <p className="text-[11px] font-bold text-[hsl(var(--warning))]">Totale tratta {row.route}: {Math.round(sum.total_percent)}% — resta {Math.round(100 - sum.total_percent)}% da assegnare.</p>
            ) : null}
          </div>
        );
      })}
      <div className="flex items-center gap-2">
        <button data-testid={`quote-add-${carrier.id}`} onClick={addRow} className="flex items-center gap-1.5 rounded-lg border border-primary px-3 py-1.5 text-xs font-extrabold text-primary"><Plus className="h-4 w-4" /> Aggiungi tratta</button>
        <button data-testid={`quote-save-${carrier.id}`} onClick={save} disabled={saving} className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-extrabold text-primary-foreground disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva</button>
        {msg ? <span className="text-xs font-bold text-[hsl(var(--success))]">{msg}</span> : null}
      </div>
    </div>
  );
}

function QuoteTab() {
  const [carriers, setCarriers] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [inviteMsg, setInviteMsg] = useState("");

  const load = async () => {
    setLoading(true);
    const [c, s] = await Promise.all([apiFetch("/dispatch/carriers"), apiFetch("/dispatch/route-summary").catch(() => [])]);
    setCarriers(c);
    setSummary(s);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!name.trim() || !email.trim()) { setInviteMsg("Inserisci ragione sociale ed email."); return; }
    try {
      const res = await apiFetch("/dispatch/carriers/invite", { method: "POST", body: JSON.stringify({ name: name.trim(), email: email.trim() }) });
      if (res.already) setInviteMsg("Vettore già presente nella tua rete.");
      else if (res.linked) setInviteMsg("Vettore collegato: account già registrato.");
      else setInviteMsg("Invito inviato via email al vettore.");
      setName(""); setEmail("");
      load();
    } catch (e) { setInviteMsg(e.message || "Errore"); }
  };

  if (loading) return <Loader />;
  return (
    <div className="space-y-4">
      <Card className="space-y-2">
        <p className="text-sm font-extrabold text-foreground">Aggiungi / invita vettore</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ragione sociale" className="min-h-[46px] flex-1 rounded-xl border border-border bg-card px-3 text-sm outline-none focus:border-primary" />
          <input data-testid="invite-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email vettore" className="min-h-[46px] flex-1 rounded-xl border border-border bg-card px-3 text-sm outline-none focus:border-primary" />
          <button data-testid="invite-submit" onClick={invite} className="flex min-h-[46px] items-center justify-center gap-1.5 rounded-xl bg-primary px-4 text-sm font-extrabold text-primary-foreground"><Mail className="h-4 w-4" /> Invita</button>
        </div>
        {inviteMsg ? <p className="text-xs font-bold text-primary">{inviteMsg}</p> : null}
      </Card>

      {carriers.length === 0 ? (
        <EmptyState icon={Users} title="Nessun vettore" text="Aggiungi i vettori e configura le quote per tratta." />
      ) : (
        carriers.map((c) => (
          <Card key={c.id} data-testid={`dispatch-carrier-${c.id}`} className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-extrabold text-foreground">{c.name}</p>
                <p className="truncate text-xs text-muted-foreground">{c.email}</p>
              </div>
              <StatusBadge status={c.status} />
            </div>
            <QuoteEditor carrier={c} summary={summary} onSaved={load} />
          </Card>
        ))
      )}
    </div>
  );
}

function ImportTab() {
  const [step, setStep] = useState("upload"); // upload | groups | preview | done
  const [importId, setImportId] = useState(null);
  const [groups, setGroups] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [report, setReport] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [mappingTargets, setMappingTargets] = useState({});
  const [discarded, setDiscarded] = useState([]);
  const [canRemap, setCanRemap] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const applyImportRes = (res) => {
    setGroups(res.groups || []);
    setReport(res.report || null);
    setColumns(res.columns || []);
    setMapping(res.mapping || {});
    setMappingTargets(res.mapping_targets || {});
    setDiscarded(res.discarded || []);
    setCanRemap(!!res.can_remap);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setError(""); setShowMap(false);
    try {
      const res = await apiUpload("/dispatch/import", file);
      setImportId(res.id); applyImportRes(res); setStep("groups");
    } catch (err) { setError(err.message || "Importazione non riuscita."); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const remap = async () => {
    setBusy(true); setError("");
    try {
      const res = await apiFetch(`/dispatch/imports/${importId}/remap`, { method: "POST", body: JSON.stringify({ mapping }) });
      applyImportRes(res); setShowMap(false);
    } catch (err) { setError(err.message || "Rimappatura non riuscita."); }
    finally { setBusy(false); }
  };

  const allocate = async () => {
    setBusy(true); setError("");
    try {
      const res = await apiFetch(`/dispatch/imports/${importId}/allocate`, { method: "POST", body: "{}" });
      setGroups(res.groups); setCarriers(res.carriers); setStep("preview");
    } catch (err) { setError(err.message || "Errore ripartizione."); }
    finally { setBusy(false); }
  };

  const setTripCarrier = (gi, ti, carrierId) => {
    setGroups((gs) => gs.map((g, i) => i !== gi ? g : { ...g, trips: g.trips.map((t, j) => j !== ti ? t : { ...t, carrier_id: carrierId || null }) }));
  };

  const setTripField = (gi, ti, field, value) => {
    setGroups((gs) => gs.map((g, i) => i !== gi ? g : { ...g, trips: g.trips.map((t, j) => j !== ti ? t : { ...t, [field]: value }) }));
  };

  // --- Giri collegati (round trips) ---
  const [routeA, setRouteA] = useState("");
  const [routeB, setRouteB] = useState("");
  const [giri, setGiri] = useState(null);

  const patchTripsByIds = (ids, patch) =>
    setGroups((gs) => gs.map((g) => ({ ...g, trips: g.trips.map((t) => ids.includes(t.id) ? { ...t, ...patch } : t) })));

  const proposeGiri = async () => {
    if (!routeA || !routeB || routeA === routeB) { setError("Scegli due tratte diverse."); return; }
    setBusy(true); setError("");
    try {
      const res = await apiFetch(`/dispatch/imports/${importId}/round-trips`, { method: "POST", body: JSON.stringify({ route_a: routeA, route_b: routeB }) });
      const withLinked = { ...res, giri: res.giri.map((g) => ({ ...g, linked: true })) };
      setGiri(withLinked);
      withLinked.giri.forEach((g) => patchTripsByIds(g.legs.map((l) => l.id), { linked_trip_group_id: g.group_id, carrier_id: g.carrier_id || null }));
    } catch (e) { setError(e.message || "Proposta giri non riuscita."); }
    finally { setBusy(false); }
  };

  const setGiroCarrier = (gi, cid) => {
    setGiri((old) => {
      const giro = old.giri[gi];
      if (giro.linked) patchTripsByIds(giro.legs.map((l) => l.id), { carrier_id: cid || null });
      return { ...old, giri: old.giri.map((g, i) => i === gi ? { ...g, carrier_id: cid || null } : g) };
    });
  };

  const toggleGiro = (gi) => {
    setGiri((old) => {
      const giro = old.giri[gi];
      const nowLinked = !giro.linked;
      patchTripsByIds(giro.legs.map((l) => l.id), nowLinked ? { linked_trip_group_id: giro.group_id, carrier_id: giro.carrier_id || null } : { linked_trip_group_id: null });
      return { ...old, giri: old.giri.map((g, i) => i === gi ? { ...g, linked: nowLinked } : g) };
    });
  };

  const confirm = async () => {
    setBusy(true); setError("");
    try {
      const rows = groups.flatMap((g) => g.trips.map((t) => ({
        pickup: t.pickup || "", delivery: t.delivery || "", date: t.date || "", time: t.time || "",
        reference: t.reference || "", po: t.po || "", cargo: t.cargo || "", notes: t.notes || "", automatic: !!t.automatic,
        carrier_id: t.carrier_id || null, linked_trip_group_id: t.linked_trip_group_id || null,
      })));
      const res = await apiFetch(`/dispatch/imports/${importId}/confirm`, { method: "POST", body: JSON.stringify({ rows }) });
      setResult(res); setStep("done");
    } catch (err) { setError(err.message || "Conferma non riuscita."); }
    finally { setBusy(false); }
  };

  if (step === "done" && result) {
    return (
      <Card className="space-y-3 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-[hsl(var(--success))]" />
        <p className="text-lg font-extrabold text-foreground">Assegnazione confermata</p>
        <p className="text-sm text-muted-foreground">{result.trips_created} viaggi creati · {result.unassigned} da assegnare</p>
        <div className="space-y-1">
          {result.distribution.map((d) => <p key={d.carrier_id} className="text-sm font-bold text-card-foreground">{d.name}: {d.count} viaggi</p>)}
        </div>
        <button onClick={() => { setStep("upload"); setGroups([]); setResult(null); setImportId(null); setReport(null); setDiscarded([]); setShowMap(false); }} className="mx-auto rounded-xl bg-primary px-4 py-2.5 text-sm font-extrabold text-primary-foreground">Nuova importazione</button>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {step === "upload" ? (
        <Card className="space-y-3 text-center">
          <FileUp className="mx-auto h-10 w-10 text-primary" />
          <p className="font-extrabold text-foreground">Importa programma viaggi</p>
          <p className="text-sm text-muted-foreground">Foto, PDF, Excel o CSV. L&apos;AI estrae i viaggi; potrai verificarli e correggerli prima della conferma.</p>
          <label data-testid="import-file-label" className="mx-auto inline-flex cursor-pointer items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-extrabold text-primary-foreground">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />} {busy ? "Analisi..." : "Carica documento"}
            <input data-testid="import-file-input" type="file" accept="image/*,.pdf,.xlsx,.xls,.csv" className="hidden" onChange={onFile} />
          </label>
          {error ? <p data-testid="import-error" className="text-sm font-bold text-destructive">{error}</p> : null}
        </Card>
      ) : null}

      {step === "groups" ? (
        <>
          {report ? (
            <Card className="space-y-2" data-testid="import-report">
              <p className="font-extrabold text-foreground">Esito analisi file</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-xl bg-muted/60 p-2 text-center"><p className="text-lg font-extrabold text-foreground" data-testid="report-read">{report.read}</p><p className="text-[11px] font-bold text-muted-foreground">Righe lette</p></div>
                <div className="rounded-xl bg-[hsl(var(--success))]/10 p-2 text-center"><p className="text-lg font-extrabold text-[hsl(var(--success))]" data-testid="report-recognized">{report.recognized}</p><p className="text-[11px] font-bold text-muted-foreground">Riconosciute</p></div>
                <div className="rounded-xl bg-[hsl(var(--warning))]/10 p-2 text-center"><p className="text-lg font-extrabold text-[hsl(var(--warning))]" data-testid="report-partial">{report.partial}</p><p className="text-[11px] font-bold text-muted-foreground">Parziali</p></div>
                <div className="rounded-xl bg-destructive/10 p-2 text-center"><p className="text-lg font-extrabold text-destructive" data-testid="report-discarded">{report.discarded}</p><p className="text-[11px] font-bold text-muted-foreground">Scartate</p></div>
              </div>
              {report.partial > 0 ? <p className="text-[11px] text-muted-foreground">Le righe parziali (dati mancanti) sono incluse: potrai completarle nell&apos;anteprima prima della conferma.</p> : null}
            </Card>
          ) : null}

          {canRemap && columns.length ? (
            <Card className="space-y-2">
              <button data-testid="toggle-mapping" onClick={() => setShowMap((v) => !v)} className="flex w-full items-center justify-between text-left">
                <span className="font-extrabold text-foreground">Mappatura colonne</span>
                <span className="text-xs font-bold text-primary">{showMap ? "Nascondi" : "Modifica"}</span>
              </button>
              {showMap ? (
                <>
                  <p className="text-[11px] text-muted-foreground">Associa ogni colonna del file al campo corretto. Il file originale non viene modificato.</p>
                  {columns.map((col) => (
                    <div key={col} className="flex items-center gap-2 border-t border-border pt-2">
                      <p className="min-w-0 flex-1 truncate text-sm font-bold text-card-foreground" title={col}>{col}</p>
                      <select data-testid={`map-col-${col}`} value={mapping[col] || "ignore"} onChange={(e) => setMapping((m) => ({ ...m, [col]: e.target.value }))} className="rounded-lg border border-border bg-card px-2 py-1.5 text-xs font-bold outline-none">
                        {Object.entries(mappingTargets).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                      </select>
                    </div>
                  ))}
                  <button data-testid="remap-button" onClick={remap} disabled={busy} className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-secondary text-sm font-extrabold text-secondary-foreground disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Ri-applica mappatura</button>
                </>
              ) : null}
            </Card>
          ) : null}

          <Card className="space-y-2">
            <p className="font-extrabold text-foreground">Viaggi riconosciuti per tratta</p>
            {groups.length ? groups.map((g) => <p key={g.route_key} className="text-sm text-card-foreground">{g.label}: <strong>{g.trips.length} viaggi</strong></p>) : <p className="text-sm text-muted-foreground">Nessuna tratta riconosciuta. Prova a correggere la mappatura delle colonne.</p>}
          </Card>

          {discarded.length ? (
            <Card className="space-y-1">
              <p className="font-extrabold text-destructive">Righe scartate ({discarded.length})</p>
              {discarded.slice(0, 20).map((d, i) => (
                <p key={i} className="text-[11px] text-muted-foreground" data-testid={`discarded-row-${i}`}><strong className="text-card-foreground">{d.reason}</strong> — {d.raw_text || "riga vuota"}</p>
              ))}
            </Card>
          ) : null}

          <button data-testid="allocate-button" onClick={allocate} disabled={busy || !groups.length} className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Wand2 className="h-5 w-5" />} Calcola ripartizione</button>
          {error ? <p className="text-sm font-bold text-destructive">{error}</p> : null}
        </>
      ) : null}

      {step === "preview" ? (
        <>
          <Eyebrow>ANTEPRIMA RIPARTIZIONE VIAGGI</Eyebrow>
          <PageSubtitle>Controlla e modifica le assegnazioni prima di confermare.</PageSubtitle>

          <Card className="space-y-2" data-testid="round-trips-card">
            <p className="font-extrabold text-foreground">Giri collegati (round trip)</p>
            <p className="text-[11px] text-muted-foreground">Collega due tratte dello stesso giro: vengono proposti MIN(A,B) giri; i viaggi in eccesso restano residui assegnabili singolarmente. Per i giri collegati, per default entrambe le tratte vanno allo stesso vettore (modificabile).</p>
            <div className="flex flex-wrap items-center gap-2">
              <select data-testid="giro-route-a" value={routeA} onChange={(e) => setRouteA(e.target.value)} className="min-w-[140px] flex-1 rounded-lg border border-border bg-card px-2 py-1.5 text-xs font-bold outline-none">
                <option value="">Tratta A…</option>
                {groups.map((g) => <option key={g.route_key} value={g.route_key}>{g.label} ({g.trips.length})</option>)}
              </select>
              <select data-testid="giro-route-b" value={routeB} onChange={(e) => setRouteB(e.target.value)} className="min-w-[140px] flex-1 rounded-lg border border-border bg-card px-2 py-1.5 text-xs font-bold outline-none">
                <option value="">Tratta B…</option>
                {groups.map((g) => <option key={g.route_key} value={g.route_key}>{g.label} ({g.trips.length})</option>)}
              </select>
              <button data-testid="giro-propose" onClick={proposeGiri} disabled={busy} className="flex min-h-[40px] items-center justify-center gap-1.5 rounded-lg bg-secondary px-3 text-xs font-extrabold text-secondary-foreground disabled:opacity-60"><Wand2 className="h-4 w-4" /> Proponi giri</button>
            </div>
            {giri ? (
              <div className="space-y-2">
                <p className="text-sm font-bold text-card-foreground" data-testid="giro-summary">{giri.count} giri collegati · residui: {giri.residui_a.length} {giri.route_a.label} / {giri.residui_b.length} {giri.route_b.label}</p>
                {giri.giri.map((g, gi) => (
                  <div key={g.group_id} data-testid={`giro-${gi}`} className="space-y-1 rounded-xl border border-border p-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-extrabold text-card-foreground">Giro {gi + 1} {g.linked ? "" : "· separato"}</p>
                      <button data-testid={`giro-toggle-${gi}`} onClick={() => toggleGiro(gi)} className="text-[11px] font-bold text-primary">{g.linked ? "Separa" : "Collega"}</button>
                    </div>
                    {g.legs.map((l) => <p key={l.id} className="truncate text-[11px] text-muted-foreground">{l.route_label} · {l.date}{l.reference ? ` · ${l.reference}` : ""}</p>)}
                    <select data-testid={`giro-carrier-${gi}`} value={g.carrier_id || ""} onChange={(e) => setGiroCarrier(gi, e.target.value)} disabled={!g.linked} className="w-full rounded-lg border border-border bg-card px-2 py-1.5 text-xs font-bold outline-none disabled:opacity-50">
                      <option value="">Da assegnare</option>
                      {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            ) : null}
          </Card>
          {groups.map((g, gi) => (
            <Card key={g.route_key} className="space-y-2">
              <p className="font-extrabold text-foreground">{g.label} — {g.trips.length} viaggi</p>
              {(g.warnings || []).map((w, i) => <p key={i} className="flex items-start gap-1.5 text-xs font-bold text-[hsl(var(--warning))]"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {w}</p>)}
              {g.trips.map((t, ti) => (
                <div key={t.id} className="space-y-1.5 border-t border-border pt-2">
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-card-foreground">{t.pickup || "—"} → {t.delivery || "—"}
                        {t.automatic ? <span className="ml-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-extrabold text-secondary-foreground">AUTO</span> : null}
                        {t.status === "partial" ? <span className="ml-1 rounded bg-[hsl(var(--warning))]/20 px-1.5 py-0.5 text-[10px] font-extrabold text-[hsl(var(--warning))]">PARZIALE</span> : null}
                        {t.linked_trip_group_id ? <span className="ml-1 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-extrabold text-primary">GIRO</span> : null}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground">{t.date} {t.reference ? `· ${t.reference}` : ""}{t.po ? ` · PO ${t.po}` : ""}</p>
                    </div>
                    <select data-testid={`preview-carrier-${t.id}`} value={t.carrier_id || ""} onChange={(e) => setTripCarrier(gi, ti, e.target.value)} className="rounded-lg border border-border bg-card px-2 py-1.5 text-xs font-bold outline-none">
                      <option value="">Da assegnare</option>
                      {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  {t.status === "partial" ? (
                    <div className="grid grid-cols-3 gap-1.5">
                      <input data-testid={`fix-pickup-${t.id}`} value={t.pickup || ""} onChange={(e) => setTripField(gi, ti, "pickup", e.target.value)} placeholder="Origine" className="rounded-lg border border-border bg-card px-2 py-1.5 text-xs outline-none" />
                      <input data-testid={`fix-delivery-${t.id}`} value={t.delivery || ""} onChange={(e) => setTripField(gi, ti, "delivery", e.target.value)} placeholder="Destinazione" className="rounded-lg border border-border bg-card px-2 py-1.5 text-xs outline-none" />
                      <input data-testid={`fix-date-${t.id}`} value={t.date || ""} onChange={(e) => setTripField(gi, ti, "date", e.target.value)} placeholder="Data" className="rounded-lg border border-border bg-card px-2 py-1.5 text-xs outline-none" />
                    </div>
                  ) : null}
                </div>
              ))}
            </Card>
          ))}
          <button data-testid="confirm-button" onClick={confirm} disabled={busy} className="flex min-h-[54px] w-full items-center justify-center gap-2 rounded-2xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} CONFERMA ASSEGNAZIONE</button>
          {error ? <p className="text-sm font-bold text-destructive">{error}</p> : null}
        </>
      ) : null}
    </div>
  );
}

function StoricoTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { apiFetch("/dispatch/imports").then(setItems).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <Loader />;
  if (items.length === 0) return <EmptyState icon={History} title="Nessuna importazione" text="Le importazioni confermate appariranno qui con la distribuzione per vettore." />;
  return (
    <div className="space-y-3">
      {items.map((it) => (
        <Card key={it.id} data-testid={`history-${it.id}`} className="space-y-1">
          <div className="flex items-center justify-between">
            <p className="truncate font-extrabold text-foreground">{it.filename}</p>
            <span className="text-[11px] font-bold text-muted-foreground">{new Date(it.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
          </div>
          <p className="text-xs text-muted-foreground">Stato: {it.status} {it.trips_created ? `· ${it.trips_created} viaggi` : ""} {it.unassigned ? `· ${it.unassigned} da assegnare` : ""}</p>
          {(it.distribution || []).map((d) => <p key={d.carrier_id} className="text-xs text-card-foreground">{d.name}: {d.count}</p>)}
        </Card>
      ))}
    </div>
  );
}

export default function DispatchPage() {
  const [tab, setTab] = useState("quote");
  return (
    <div className="space-y-3">
      <Eyebrow>RIPARTIZIONE AUTOMATICA</Eyebrow>
      <PageTitle>Ripartizione viaggi</PageTitle>
      <PageSubtitle>Configura le quote per tratta, importa il programma e distribuisci i viaggi tra i vettori.</PageSubtitle>
      <div className="flex gap-2 overflow-x-auto py-1">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} data-testid={`dispatch-tab-${k}`} onClick={() => setTab(k)} className={cn("flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold", tab === k ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground")}>
            <Icon className="h-4 w-4" /> {l}
          </button>
        ))}
      </div>
      {tab === "quote" ? <QuoteTab /> : tab === "import" ? <ImportTab /> : <StoricoTab />}
    </div>
  );
}
